import { useParams, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';

type ParamKind = 'param' | 'searchParam' | 'paramThenSearchParam';
type ReturnType = 'string' | 'number' | 'stringArray' | 'numberArray';

function useCustomParam<T, R = T | null>(
  key: string,
  kind: ParamKind,
  returnType: ReturnType,
  defaultValue: T | null,
): R {
  const params = useParams();
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    let paramValue: string | null = null;

    switch (kind) {
      case 'param':
        paramValue = params[key] ?? null;
        break;
      case 'searchParam':
        paramValue = searchParams.get(key);
        break;
      case 'paramThenSearchParam':
        paramValue = params[key] ?? searchParams.get(key);
        break;
    }

    if (paramValue === null) {
      return defaultValue as R;
    }

    try {
      switch (returnType) {
        case 'number':
          return isNaN(Number(paramValue))
            ? (defaultValue as R)
            : (Number(paramValue) as R);
        case 'string':
          return paramValue as unknown as R;
        case 'numberArray':
          return paramValue
            .split(',')
            .map((str) => {
              const num = Number(str);
              return isNaN(num) ? null : num;
            })
            .filter((num) => num !== null) as unknown as R;
        case 'stringArray':
          return paramValue.split(',') as unknown as R;
        default:
          return defaultValue as R;
      }
    } catch (error) {
      console.error('Error parsing parameter:', error);
      return defaultValue as R;
    }
  }, [key, kind, params, searchParams]);
}

export default useCustomParam;
